import { createFileRoute, Link } from "@tanstack/react-router";
import {
  ArrowLeft,
  CheckCircle2,
  FileText,
  KeyRound,
  Landmark,
  Megaphone,
  Scale,
  ShieldCheck,
  Tag,
  Wrench,
} from "lucide-react";

export const Route = createFileRoute("/guia-del-arrendador")({
  head: () => ({
    meta: [
      { title: "Guía del arrendador — Bizaclia" },
      {
        name: "description",
        content:
          "Guía práctica para arrendadores de locales comerciales. Aprende a preparar, publicar y gestionar tu alquiler de forma segura.",
      },
      { property: "og:title", content: "Guía del arrendador — Bizaclia" },
      {
        property: "og:description",
        content:
          "Todo lo que necesitas saber para alquilar tu local comercial con éxito.",
      },
      { property: "og:url", content: "/guia-del-arrendador" },
    ],
    links: [{ rel: "canonical", href: "/guia-del-arrendador" }],
  }),
  component: GuiaArrendadorPage,
});

const pasos = [
  {
    icon: Wrench,
    title: "1. Prepara el local",
    text: "Revisa el estado del inmueble, soluciona averías menores y asegúrate de que cumple la normativa de accesibilidad y seguridad. Un local en buen estado atrae más interesados y permite fijar un precio más competitivo.",
  },
  {
    icon: Tag,
    title: "2. Fija el precio de mercado",
    text: "Investiga precios de locales similares en tu zona. Ten en cuenta metros cuadrados, ubicación, afluencia peatonal y equipamiento. Un precio ajustado reduce el tiempo de vacancia.",
  },
  {
    icon: Megaphone,
    title: "3. Publica en Bizaclia",
    text: "Crea un anuncio con fotos de calidad, descripción detallada y datos clave (superficie, altura, terraza, aforo, licencia de actividad). Cuanta más información, menos consultas irrelevantes.",
  },
  {
    icon: ShieldCheck,
    title: "4. Selecciona al inquilino",
    text: "Solicita documentación (DNI, actividad comercial prevista, referencias bancarias). Valora su solvencia y experiencia. Un buen inquilino evita impagos y conflictos futuros.",
  },
  {
    icon: FileText,
    title: "5. Redacta el contrato",
    text: "Incluye duración, renta, fianza, gastos (IBI, comunidad, suministros), uso permitido, obra y condiciones de resolución. Recomendamos asesoría jurídica para contratos de locales comerciales.",
  },
  {
    icon: KeyRound,
    title: "6. Entrega y fianza",
    text: "Formaliza la fianza legal (generalmente 2 mensualidades en alquiler de local). Haz un inventario detallado con fotos y entrega de llaves firmado por ambas partes.",
  },
];

const consejos = [
  {
    icon: Scale,
    title: "Verifica la licencia de actividad",
    text: "Asegúrate de que el local tiene licencia compatible con el negocio que instalará el inquilino. Cambios de uso pueden ser costosos y lentos.",
  },
  {
    icon: Landmark,
    title: "Clarifica quién paga qué",
    text: "En alquileres comerciales suele negociarse quién asume el IBI, la comunidad, el seguro del local y las reparaciones estructurales. Deja por escrito todo lo acordado.",
  },
  {
    icon: CheckCircle2,
    title: "Revisa la solvencia",
    text: "Puedes pedir un aval bancario o un seguro de impago para protegerte. En locales comerciales, la renta suele ser superior a la residencial, por lo que el riesgo también lo es.",
  },
];

function GuiaArrendadorPage() {
  return (
    <main className="mx-auto max-w-5xl px-4 py-10">
      <Link
        to="/"
        className="mb-6 inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" /> Volver al inicio
      </Link>

      <header className="mb-10">
        <h1 className="text-4xl font-bold tracking-tight">Guía del arrendador</h1>
        <p className="mt-2 text-muted-foreground max-w-2xl">
          Todo lo que necesitas saber para alquilar tu local comercial de forma segura, rápida y rentable.
        </p>
      </header>

      {/* Steps */}
      <section className="mb-14">
        <h2 className="text-2xl font-semibold mb-6">Pasos para alquilar tu local</h2>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {pasos.map((p) => (
            <div
              key={p.title}
              className="rounded-xl border border-border bg-background p-5 transition-shadow hover:shadow-sm"
            >
              <div className="flex items-center gap-3 mb-3">
                <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/10">
                  <p.icon className="h-4.5 w-4.5 text-primary" />
                </div>
                <h3 className="font-semibold">{p.title}</h3>
              </div>
              <p className="text-sm text-muted-foreground leading-relaxed">{p.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Tips */}
      <section className="mb-14">
        <h2 className="text-2xl font-semibold mb-6">Consejos clave</h2>
        <div className="space-y-4">
          {consejos.map((c) => (
            <div
              key={c.title}
              className="rounded-lg border border-border bg-background p-5"
            >
              <div className="flex items-start gap-3">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10 mt-0.5">
                  <c.icon className="h-4.5 w-4.5 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold">{c.title}</h3>
                  <p className="mt-1 text-sm text-muted-foreground leading-relaxed">{c.text}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="mb-14">
        <h2 className="text-2xl font-semibold mb-6">Preguntas frecuentes</h2>
        <div className="space-y-3">
          {[
            {
              q: "¿Cuánto puede durar un contrato de alquiler de local comercial?",
              a: "La Ley de Arrendamientos Urbanos (LAU) permite la libre pacto de duración entre partes. Lo habitual son entre 5 y 10 años, con posibilidad de prórroga si ninguna parte notifica lo contrario con 4 meses de antelación.",
            },
            {
              q: "¿Qué fianza debo pedir?",
              a: "Legalmente, la fianza mínima para locales comerciales es de 2 mensualidades de renta. Muchos arrendadores solicitan aval bancario o seguro de impago como garantía adicional.",
            },
            {
              q: "¿Puedo alquilar un local sin licencia de actividad?",
              a: "Sí, pero el inquilino deberá tramitarla a su nombre para el uso que vaya a darle. Asegúrate de que el uso proyectado es compatible con la calificación urbanística del inmueble.",
            },
            {
              q: "¿Quién paga las reparaciones?",
              a: "Las reparaciones estructurales y del inmueble son habitualmente del arrendador. Las reparaciones de conservación y mantenimiento derivadas del uso comercial suelen ser del inquilino. Lo ideal es pactarlo claramente en el contrato.",
            },
          ].map((faq) => (
            <details
              key={faq.q}
              className="rounded-lg border border-border bg-background group"
            >
              <summary className="flex cursor-pointer items-center justify-between p-5 text-sm font-semibold select-none">
                {faq.q}
                <span className="ml-4 text-muted-foreground group-open:rotate-180 transition-transform">
                  ▼
                </span>
              </summary>
              <div className="px-5 pb-5 text-sm text-muted-foreground leading-relaxed">
                {faq.a}
              </div>
            </details>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="rounded-xl border border-border bg-background p-6 md:p-8 text-center">
        <h2 className="text-xl font-semibold">¿Listo para publicar tu local?</h2>
        <p className="mt-2 text-muted-foreground text-sm max-w-lg mx-auto">
          Publica tu anuncio gratis en Bizaclia y empieza a recibir consultas de inquilinos interesados.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-3">
          <Link
            to="/mybizaclia"
            className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            Publicar local
          </Link>
          <Link
            to="/contacto"
            className="inline-flex items-center gap-2 rounded-md border border-border bg-background px-4 py-2 text-sm font-medium hover:bg-muted"
          >
            Hablar con un asesor
          </Link>
        </div>
      </section>
    </main>
  );
}
